import { useEffect, useState } from "react";

import type { StepsDataPoint } from "~/api/steps";
import { getDateRange, getGroupByInterval, getStepsData } from "~/api/steps";

interface UseStepsDataProps {
  patientId: number;
  timeRange: string;
  navigationOffset?: number;
  selectedDate?: Date;
}

interface UseStepsDataReturn {
  data: StepsDataPoint[];
  metadata: {
    avg: number;
  } | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useStepsData = ({
  patientId,
  timeRange,
  navigationOffset = 0,
  selectedDate,
}: UseStepsDataProps): UseStepsDataReturn => {
  const [data, setData] = useState<StepsDataPoint[]>([]);
  const [metadata, setMetadata] = useState<{
    avg: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const dateRange = getDateRange(timeRange, navigationOffset, selectedDate);
      const groupByInterval = getGroupByInterval(timeRange);

      const response = await getStepsData({
        patient_id: patientId,
        group_by_interval: groupByInterval,
        start_date: dateRange.start_date,
        stop_date: dateRange.stop_date,
      });

      setData(response.data);
      setMetadata({
        avg: response.metadata.avg,
      });
    } catch (err) {
      console.error("Error fetching steps data:", err);
      setError("Failed to fetch steps data");
      setData([]);
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) {
      void fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, timeRange, navigationOffset, selectedDate]);

  return {
    data,
    metadata,
    loading,
    error,
    refetch: fetchData,
  };
};
